import { useMutation } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/core';
import { gql } from 'apollo-boost';
import classnames from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import theme from 'src/theme';
import * as yup from 'yup';
import Button from '~components/button';
import Typography from '~components/typography';
import { useReplaceSuiteStudentToCustomer } from '~containers/suite-config/suite-context';
import useDisplayResponseErrorToast from '~utils/use-display-response-error-toast';
import useDisplayToast from '~utils/use-display-toast';
import {
  FieldAccountEmail,
  FieldFirstName,
  FieldGender,
  FieldLastName,
  FieldMiddleName,
  FieldPartnerReferenceId,
} from './form-fields';

const useStyles = makeStyles({
  root: {
    margin: '0 auto',
    width: 320,
    paddingBottom: 40,
  },

  form: {
    marginTop: 32,
    display: 'grid',
    rowGap: '20px',
  },

  actions: {
    marginTop: 32,
    display: 'grid',
    justifyItems: 'center',
    rowGap: '24px',
  },

  createTypeBtn: {
    padding: '8px 0',
    width: 154,
    border: `1px solid ${theme.colors.greyCc}`,
    borderRadius: '4px',
    '&:first-child': {
      marginRight: 8,
    },
  },

  selectedCreateTypeBtn: { borderColor: theme.colors.brandAction, color: theme.colors.brandAction },

  createTypeRoot: {
    ...theme.flex.center,
  },
});

export const addStudentToNominationPlanGqlMutation = gql`
  mutation AddStudentToNominationPlan($propertyId: ID!, $input: addStudentToNominationPlanInput!) {
    addStudentToNominationPlan(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const ADD_PLACEHOLDER_STUDENT_TO_NOMINATION_PLAN = gql`
  mutation AddStudentToNominationPlan(
    $propertyId: ID!
    $input: addPlaceholderStudentToNominationPlanInput!
  ) {
    addPlaceholderStudentToNominationPlan(propertyId: $propertyId, input: $input) {
      student {
        firstName
        middleName
        lastName
        email
        gender
      }
      booking {
        id
        partnerReferenceId
      }
    }
  }
`;

export const PLACEHOLDER_STUDENT = 'placeholder_student';
export const STUDENT_WITH_DETAIL = 'student_with_detail';

function CreateStudentModalContent(props) {
  const { t } = useTranslation('nomination-manage-students');
  const displayResponseErrorToast = useDisplayResponseErrorToast();
  const displayToast = useDisplayToast();
  const classes = useStyles();
  const replaceSuiteStudentToCustomer = useReplaceSuiteStudentToCustomer();

  const {
    nominationPlanId,
    propertyId,
    onBack,
    onCompleted,
    onCreateNewPlaceHolderStudent,
    createType,
  } = props;

  const initialValues = {
    email: '',
    firstName: '',
    middleName: '',
    lastName: '',
    partnerReferenceId: '',
    gender: '',
    createType: createType || PLACEHOLDER_STUDENT,
  };

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        createType: yup.string(),
        email: yup.string().when('createType', {
          is: (value) => value === STUDENT_WITH_DETAIL,
          then: yup.string().email().label(t('account_email')).required(),
        }),
        firstName: yup.string().when('createType', {
          is: (value) => value === STUDENT_WITH_DETAIL,
          then: yup.string().label(t('first_name')).required(),
        }),
        lastName: yup.string().when('createType', {
          is: (value) => value === STUDENT_WITH_DETAIL,
          then: yup.string().label(t('last_name')).required(),
        }),
        partnerReferenceId: yup.string().when('createType', {
          is: (value) => value === STUDENT_WITH_DETAIL,
          then: yup.string().label(t('partner_reference_id')).required(),
        }),
        gender: yup.string().when('createType', {
          is: (value) => value === STUDENT_WITH_DETAIL,
          then: yup.string().label(t('gender')).nullable().required(),
        }),
      }),
    [t]
  );

  const [addStudentToNominationPlanMutation] = useMutation(addStudentToNominationPlanGqlMutation);

  const [addPlaceholderStudentToNominationPlan] = useMutation(
    ADD_PLACEHOLDER_STUDENT_TO_NOMINATION_PLAN,
    {
      onCompleted: (complete) => {
        onCreateNewPlaceHolderStudent({
          addPlaceholderStudentToNominationPlan: complete?.addPlaceholderStudentToNominationPlan,
        });
        displayToast('success-added', replaceSuiteStudentToCustomer(t('student'), t('customer')));
      },
      onError: displayResponseErrorToast,
    }
  );

  const handSubmitCreatePlaceholderStudent = async (values) => {
    await addPlaceholderStudentToNominationPlan({
      variables: {
        propertyId,
        input: {
          gender: values?.gender?.value || undefined,
          partnerReferenceId: values?.partnerReferenceId || undefined,
          nominationPlanId,
        },
      },
    });
  };

  const onSubmitNewDetailsStudent = useCallback(
    async (values, { setFieldError }) => {
      try {
        await addStudentToNominationPlanMutation({
          variables: {
            propertyId,
            input: {
              nominationPlanId,
              gender: values.gender.value,
              partnerReferenceId: values.partnerReferenceId,
              studentInfo: {
                email: values.email,
                firstName: values.firstName,
                middleName: values.middleName === '' ? null : values.middleName,
                lastName: values.lastName,
              },
            },
          },
        });
        displayToast('success-added', replaceSuiteStudentToCustomer(t('student'), t('customer')));
        if (onCompleted) onCompleted();
      } catch (error) {
        if (error.message.includes('STUDENT_USER_ALREADY_EXISTS')) {
          setFieldError('email', t('this_account_email_already_exists'));
        } else {
          displayResponseErrorToast(error);
        }
      }
    },
    [
      addStudentToNominationPlanMutation,
      displayResponseErrorToast,
      displayToast,
      nominationPlanId,
      onCompleted,
      propertyId,
      replaceSuiteStudentToCustomer,
      t,
    ]
  );

  const onSubmit = (values, formikHelpers) => {
    if (values?.createType === STUDENT_WITH_DETAIL)
      return onSubmitNewDetailsStudent(values, formikHelpers);
    handSubmitCreatePlaceholderStudent(values);
  };

  const formik = useFormik({ initialValues, validationSchema, onSubmit, validateOnBlur: true });

  const renderTitle = () => (
    <Typography variant="h2" align="center">
      {replaceSuiteStudentToCustomer(t('new_student'), t('new_customer'))}
    </Typography>
  );

  const renderCreateTypeItem = (key) => (
    <Button
      onClick={() => {
        formik.resetForm();
        formik.setFieldValue('createType', key);
      }}
      variant="text"
      className={classnames(classes.createTypeBtn, {
        [classes.selectedCreateTypeBtn]: key === formik.values.createType,
      })}
    >
      <Typography
        variant="body1"
        themeColor={key === formik.values.createType ? 'brandAction' : 'grey66'}
      >
        {t(key)}
        {key === PLACEHOLDER_STUDENT
          ? replaceSuiteStudentToCustomer(t('placeholder_student'), t('placeholder_customer'))
          : t(key)}
      </Typography>
    </Button>
  );

  const renderCreateType = () => (
    <div className={classes.createTypeRoot}>
      {renderCreateTypeItem(STUDENT_WITH_DETAIL)}
      {renderCreateTypeItem(PLACEHOLDER_STUDENT)}
    </div>
  );

  const renderCertainInfo = () => (
    <>
      <FieldAccountEmail propertyId={propertyId} />
      <FieldFirstName />
      <FieldMiddleName />
      <FieldLastName />
      <FieldPartnerReferenceId />
      <FieldGender />
    </>
  );

  const renderAnyInfo = () => (
    <>
      <FieldPartnerReferenceId isOptional />
      <FieldGender isOptional />
    </>
  );

  const renderForm = () => (
    <div className={classes.form}>
      {renderCreateType()}
      {formik.values.createType === STUDENT_WITH_DETAIL && renderCertainInfo()}
      {formik.values.createType === PLACEHOLDER_STUDENT && renderAnyInfo()}
    </div>
  );

  const renderAction = () => (
    <div className={classes.actions}>
      <Button
        color="primary"
        size="large"
        style={{ width: '100%' }}
        loading={formik.isSubmitting}
        onClick={formik.submitForm}
      >
        {replaceSuiteStudentToCustomer(t('add_student_to_plan'), t('add_customer_to_plan'))}
      </Button>
      <Button variant="text" onClick={onBack}>
        {t('back_to_search')}
      </Button>
    </div>
  );

  return (
    <FormikProvider value={formik}>
      <div className={classes.root}>
        {renderTitle()}
        {renderForm()}
        {renderAction()}
      </div>
    </FormikProvider>
  );
}

CreateStudentModalContent.propTypes = {
  nominationPlanId: PropTypes.string.isRequired,
  propertyId: PropTypes.string.isRequired,
  onBack: PropTypes.func,
  onCompleted: PropTypes.func,
  onCreateNewPlaceHolderStudent: PropTypes.func,
  createType: PropTypes.string,
};

export default CreateStudentModalContent;
